import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { counterpartyService } from '../services/counterpartyService';
import { expenseService } from '../services/expenseService';
import { Counterparty } from '../types/counterparty';
import { Currency } from '../types/invoice';
import { CreateExpenseRequest, Expense, ExpenseStatus } from '../types/expense';
import { useTranslation } from '../hooks/useTranslation';

interface ExpensesTabProps {
  companyId: string;
}

const ExpensesTab: React.FC<ExpensesTabProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | ExpenseStatus>('ALL');
  const [counterpartyFilter, setCounterpartyFilter] = useState<'ALL' | string>('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);

  const { data: counterparties = [] } = useQuery({
    queryKey: ['counterparties', companyId],
    queryFn: () => counterpartyService.getCompanyCounterparties(companyId)
  });

  const params = useMemo(() => {
    const p = new URLSearchParams({
      page: '0',
      size: '200'
    });
    if (searchTerm.trim()) {
      p.set('searchTerm', searchTerm.trim());
    }
    if (statusFilter !== 'ALL') {
      p.set('status', statusFilter);
    }
    if (counterpartyFilter !== 'ALL') {
      p.set('counterpartyId', counterpartyFilter);
    }
    return p;
  }, [searchTerm, statusFilter, counterpartyFilter]);

  React.useEffect(() => {
    const key = `preselect_expense_counterparty_${companyId}`;
    const preselectedCounterpartyId = sessionStorage.getItem(key);
    if (preselectedCounterpartyId) {
      setCounterpartyFilter(preselectedCounterpartyId);
      sessionStorage.removeItem(key);
    }
  }, [companyId]);

  const { data: paginated, isLoading } = useQuery({
    queryKey: ['expenses', companyId, params.toString()],
    queryFn: () => expenseService.getPaginated(companyId, params)
  });

  const expenses: Expense[] = paginated?.content || [];

  const createMutation = useMutation({
    mutationFn: (payload: CreateExpenseRequest) => expenseService.create(companyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses', companyId] });
      setIsModalOpen(false);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CreateExpenseRequest }) =>
      expenseService.update(companyId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses', companyId] });
      setIsModalOpen(false);
      setEditingExpense(null);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => expenseService.delete(companyId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['expenses', companyId] })
  });

  const uploadMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => expenseService.uploadFile(companyId, id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['expenses', companyId] })
  });

  const deleteFileMutation = useMutation({
    mutationFn: (id: string) => expenseService.deleteFile(companyId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['expenses', companyId] })
  });

  const handleDelete = (expense: Expense) => {
    const confirmed = window.confirm(
      t('companies.expenses.deleteConfirm') || `Удалить расход "${expense.documentNumber}"?`
    );
    if (!confirmed) return;
    deleteMutation.mutate(expense.id);
  };

  const handleDownloadFile = async (expense: Expense) => {
    try {
      const blob = await expenseService.downloadFile(companyId, expense.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', expense.originalFileName || `expense-${expense.documentNumber}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(t('common.error') || 'Ошибка');
    }
  };

  const handleUploadFile = (expense: Expense, file?: File) => {
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      window.alert(t('companies.expenses.fileTypeError') || 'Разрешены только PDF, PNG, JPG, JPEG');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      window.alert(t('companies.expenses.fileSizeError') || 'Размер файла не должен превышать 10MB');
      return;
    }
    uploadMutation.mutate({ id: expense.id, file });
  };

  const formatDate = (value?: string) => {
    if (!value) return '-';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
  };

  const formatAmount = (amount?: number, currency?: string) => {
    if (amount === null || amount === undefined) return '-';
    return `${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ''}`.trim();
  };

  const getStatusLabel = (status: ExpenseStatus) =>
    t(`companies.expenses.statuses.${status}`) || status;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('companies.tabs.expenses') || 'Расходы'}
        </h3>
        <button
          onClick={() => {
            setEditingExpense(null);
            setIsModalOpen(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('companies.expenses.add') || 'Добавить расход'}
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('common.search') || 'Поиск'}</p>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('companies.expenses.search') || 'Поиск по номеру, названию, контрагенту...'}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.status') || 'Статус'}</p>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'ALL' | ExpenseStatus)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="ALL">{t('common.all') || 'Все'}</option>
              {Object.values(ExpenseStatus).map((status) => (
                <option key={status} value={status}>
                  {getStatusLabel(status)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.counterparty') || 'Контрагент'}</p>
            <select
              value={counterpartyFilter}
              onChange={(e) => setCounterpartyFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="ALL">{t('companies.expenses.allCounterparties') || 'Все контрагенты'}</option>
              {counterparties.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-500 flex items-center">
            {(t('companies.expenses.total') || 'Всего') + ': ' + (paginated?.totalElements ?? 0)}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-gray-600">{t('common.loading') || 'Загрузка...'}</div>
        ) : expenses.length === 0 ? (
          <div className="p-6 text-gray-600">{t('companies.expenses.empty') || 'Расходы не найдены'}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2">{t('companies.expenses.number') || 'Номер'}</th>
                  <th className="text-left px-3 py-2">{t('companies.expenses.counterparty') || 'Контрагент'}</th>
                  <th className="text-left px-3 py-2">{t('companies.expenses.date') || 'Дата'}</th>
                  <th className="text-left px-3 py-2">{t('companies.expenses.amount') || 'Сумма'}</th>
                  <th className="text-left px-3 py-2">{t('companies.expenses.status') || 'Статус'}</th>
                  <th className="text-left px-3 py-2">{t('companies.expenses.file') || 'Файл'}</th>
                  <th className="text-right px-3 py-2">{t('common.actions') || 'Действия'}</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">{expense.documentNumber}</td>
                    <td className="px-3 py-2">{expense.counterpartyName}</td>
                    <td className="px-3 py-2">{formatDate(expense.issueDate)}</td>
                    <td className="px-3 py-2">{formatAmount(expense.grossAmount, expense.currency)}</td>
                    <td className="px-3 py-2">{getStatusLabel(expense.status)}</td>
                    <td className="px-3 py-2">
                      {expense.originalFileName ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownloadFile(expense)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {expense.originalFileName}
                          </button>
                          <button
                            onClick={() => deleteFileMutation.mutate(expense.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            {t('common.delete') || 'Удалить'}
                          </button>
                        </div>
                      ) : (
                        <label className="text-blue-600 hover:text-blue-800 cursor-pointer">
                          {t('companies.expenses.uploadFile') || 'Загрузить'}
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => handleUploadFile(expense, e.target.files?.[0])}
                          />
                        </label>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right space-x-2">
                      <button
                        onClick={() => {
                          setEditingExpense(expense);
                          setIsModalOpen(true);
                        }}
                        className="px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        {t('common.edit') || 'Редактировать'}
                      </button>
                      <button
                        onClick={() => handleDelete(expense)}
                        className="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                      >
                        {t('common.delete') || 'Удалить'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <ExpenseFormModal
          counterparties={counterparties}
          preselectedCounterpartyId={counterpartyFilter !== 'ALL' ? counterpartyFilter : undefined}
          initial={editingExpense}
          isLoading={createMutation.isPending || updateMutation.isPending}
          onClose={() => {
            setIsModalOpen(false);
            setEditingExpense(null);
          }}
          onSubmit={(payload) => {
            if (editingExpense) {
              updateMutation.mutate({ id: editingExpense.id, payload });
              return;
            }
            createMutation.mutate(payload);
          }}
        />
      )}
    </div>
  );
};

interface ExpenseFormModalProps {
  counterparties: Counterparty[];
  preselectedCounterpartyId?: string;
  initial: Expense | null;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateExpenseRequest) => void;
}

const ExpenseFormModal: React.FC<ExpenseFormModalProps> = ({
  counterparties,
  preselectedCounterpartyId,
  initial,
  isLoading,
  onClose,
  onSubmit
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreateExpenseRequest>({
    counterpartyId: initial?.counterpartyId || preselectedCounterpartyId || counterparties[0]?.id || '',
    documentNumber: initial?.documentNumber || '',
    title: initial?.title || '',
    issueDate: initial?.issueDate || new Date().toISOString().split('T')[0],
    dueDate: initial?.dueDate || '',
    paymentDate: initial?.paymentDate || '',
    currency: initial?.currency || Currency.PLN,
    netAmount: initial?.netAmount,
    taxAmount: initial?.taxAmount,
    grossAmount: initial?.grossAmount,
    category: initial?.category || '',
    notes: initial?.notes || '',
    status: initial?.status || ExpenseStatus.NEW
  });

  React.useEffect(() => {
    if (!initial && preselectedCounterpartyId) {
      setForm((prev) => ({ ...prev, counterpartyId: preselectedCounterpartyId }));
    }
  }, [initial, preselectedCounterpartyId]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.counterpartyId || !form.documentNumber || !form.issueDate) {
      window.alert(t('common.fillRequired') || 'Заполните обязательные поля');
      return;
    }
    onSubmit(form);
  };

  const getStatusLabel = (status: ExpenseStatus) =>
    t(`companies.expenses.statuses.${status}`) || status;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <form onSubmit={submit} className="p-5 space-y-4">
          <h4 className="text-lg font-semibold">
            {initial ? (t('companies.expenses.edit') || 'Редактировать расход') : (t('companies.expenses.add') || 'Добавить расход')}
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.counterparty') || 'Контрагент'} *</p>
              <select
                value={form.counterpartyId}
                onChange={(e) => setForm({ ...form, counterpartyId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              >
                {counterparties.length === 0 ? (
                  <option value="">{t('companies.expenses.noCounterparties') || 'Нет контрагентов'}</option>
                ) : (
                  counterparties.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.number') || 'Номер'} *</p>
              <input
                value={form.documentNumber}
                onChange={(e) => setForm({ ...form, documentNumber: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.title') || 'Название'}</p>
              <input
                value={form.title || ''}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.status') || 'Статус'}</p>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as ExpenseStatus })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {Object.values(ExpenseStatus).map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.issueDate') || 'Дата документа'} *</p>
              <input
                type="date"
                value={form.issueDate}
                onChange={(e) => setForm({ ...form, issueDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.dueDate') || 'Срок оплаты'}</p>
              <input
                type="date"
                value={form.dueDate || ''}
                onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.paymentDate') || 'Дата фактической оплаты'}</p>
              <input
                type="date"
                value={form.paymentDate || ''}
                onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.currency') || 'Валюта'}</p>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value as Currency })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                {Object.values(Currency).map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.netAmount') || 'Сумма нетто'}</p>
              <input
                type="number"
                step="0.01"
                value={form.netAmount ?? ''}
                onChange={(e) => setForm({ ...form, netAmount: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.taxAmount') || 'Сумма налога'}</p>
              <input
                type="number"
                step="0.01"
                value={form.taxAmount ?? ''}
                onChange={(e) => setForm({ ...form, taxAmount: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.grossAmount') || 'Сумма брутто'}</p>
              <input
                type="number"
                step="0.01"
                value={form.grossAmount ?? ''}
                onChange={(e) => setForm({ ...form, grossAmount: e.target.value ? Number(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">{t('companies.expenses.category') || 'Категория'}</p>
              <input
                value={form.category || ''}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <textarea
            value={form.notes || ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder={t('companies.expenses.notes') || 'Заметки'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            rows={3}
          />

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              {t('common.cancel') || 'Отмена'}
            </button>
            <button
              type="submit"
              disabled={isLoading || counterparties.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? (t('common.saving') || 'Сохранение...') : (t('common.save') || 'Сохранить')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpensesTab;

